/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");
/* HustleEats Phase 2.C — Deposit-hold bookings tab.
   Restaurant sets when a deposit hold applies + reviews the booking list. */
export default function DepositsTab({ venueSlug }) {
  const [rules, setRules] = useState({
    enabled: false, per_cover_cents: 2500,
    apply_to_party_size_gte: 8,
    apply_to_days_of_week: [5, 6, 7],
    apply_to_hours: [18, 19, 20],
    cancel_window_hours: 24,
  });
  const [bookings, setBookings] = useState([]);
  const [msg, setMsg] = useState(null);

  const refresh = async () => {
    const [rRes, bRes] = await Promise.all([
      axios.get(`${API}/api/hustleeats/deposits/rules/${venueSlug}`, { withCredentials: true }),
      axios.get(`${API}/api/hustleeats/deposits/bookings/${venueSlug}`, { withCredentials: true }),
    ]);
    setRules({ ...rules, ...rRes.data });
    setBookings(bRes.data || []);
  };
  useEffect(() => { refresh(); }, [venueSlug]);

  const saveRules = async (e) => {
    e.preventDefault();
    setMsg(null);
    try {
      await axios.put(
        `${API}/api/hustleeats/deposits/rules/${venueSlug}`,
        rules,
        { withCredentials: true },
      );
      setMsg({ kind: "ok", text: "Deposit rules saved." });
    } catch (e) {
      setMsg({ kind: "err", text: e?.response?.data?.detail || "Failed." });
    }
  };

  const markShowed = async (id) => {
    await axios.post(
      `${API}/api/hustleeats/deposits/bookings/${id}/mark-showed`,
      {}, { withCredentials: true },
    );
    refresh();
  };
  const markNoShow = async (id) => {
    if (!window.confirm("Capture the full deposit? This charges the diner's card.")) return;
    try {
      await axios.post(
        `${API}/api/hustleeats/deposits/bookings/${id}/mark-no-show`,
        {}, { withCredentials: true },
      );
      refresh();
    } catch (e) {
      setMsg({ kind: "err", text: e?.response?.data?.detail || "No-show capture failed." });
    }
  };

  const toggleDay = (d) => {
    const days = rules.apply_to_days_of_week.includes(d)
      ? rules.apply_to_days_of_week.filter((x) => x !== d)
      : [...rules.apply_to_days_of_week, d].sort();
    setRules({ ...rules, apply_to_days_of_week: days });
  };

  return (
    <div className="ph2-tabbody">
      <h3 className="ph2-h3">When to require a deposit hold</h3>
      <form onSubmit={saveRules} className="he-form">
        <label className="he-form__field">
          <span>
            <input
              type="checkbox"
              checked={!!rules.enabled}
              onChange={(e) => setRules({ ...rules, enabled: e.target.checked })}
              data-testid="dep-enabled"
              style={{ width: 16, height: 16, marginRight: 8 }}
            />
            Enable deposit holds for this venue
          </span>
        </label>
        <div className="he-form__row">
          <label className="he-form__field">
            <span>Deposit per cover ($)</span>
            <input
              type="number"
              min={0}
              max={500}
              value={(rules.per_cover_cents || 0) / 100}
              onChange={(e) => setRules({ ...rules, per_cover_cents: Number(e.target.value) * 100 })}
              data-testid="dep-percover"
            />
          </label>
          <label className="he-form__field">
            <span>Min party size</span>
            <input
              type="number"
              min={1}
              max={50}
              value={rules.apply_to_party_size_gte}
              onChange={(e) => setRules({ ...rules, apply_to_party_size_gte: Number(e.target.value) })}
            />
          </label>
          <label className="he-form__field">
            <span>Free-cancel window (hrs)</span>
            <input
              type="number"
              min={0}
              max={168}
              value={rules.cancel_window_hours}
              onChange={(e) => setRules({ ...rules, cancel_window_hours: Number(e.target.value) })}
            />
          </label>
        </div>
        <fieldset className="he-form__field">
          <span>Days</span>
          <div className="he-daygrid">
            {[["Mon", 1], ["Tue", 2], ["Wed", 3], ["Thu", 4], ["Fri", 5], ["Sat", 6], ["Sun", 7]].map(([label, d]) => (
              <label key={d} className={`he-day ${rules.apply_to_days_of_week.includes(d) ? "is-on" : ""}`}>
                <input
                  type="checkbox"
                  checked={rules.apply_to_days_of_week.includes(d)}
                  onChange={() => toggleDay(d)}
                />
                {label}
              </label>
            ))}
          </div>
        </fieldset>
        <button type="submit" className="he-card__cta" data-testid="dep-save-btn">Save rules</button>
        {msg && <div className={`he-admin__msg he-admin__msg--${msg.kind}`}>{msg.text}</div>}
      </form>

      <h3 className="ph2-h3" style={{ marginTop: 32 }}>Bookings ({bookings.length})</h3>
      <div className="ph2-bookings">
        {bookings.length === 0 && (
          <div className="he-state" data-testid="dep-bookings-empty">No bookings yet.</div>
        )}
        {bookings.map((b) => (
          <div className="ph2-booking" key={b.id} data-testid={`dep-booking-${b.id}`}>
            <div>
              <div className="ph2-booking__head">{b.diner_name} · {b.party_size} pax</div>
              <div className="ph2-booking__sub">
                {new Date(b.booked_for).toLocaleString("en-AU", { timeZone: "Australia/Brisbane" })}
              </div>
            </div>
            <div className="ph2-booking__money">
              <div><span>Hold</span><strong>${((b.deposit_cents || 0) / 100).toFixed(2)}</strong></div>
              <div><span>Status</span><strong>{b.deposit_status || "—"}</strong></div>
            </div>
            <div className="ph2-booking__actions">
              {b.status === "held" && (
                <>
                  <button className="ph2-btn ph2-btn--ghost" onClick={() => markShowed(b.id)}>
                    Showed up
                  </button>
                  <button className="ph2-btn ph2-btn--danger" onClick={() => markNoShow(b.id)}>
                    No-show · capture
                  </button>
                </>
              )}
              <span className={`he-chip he-chip--${b.status === "held" ? "deal" : "soldout"}`}>{b.status}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
